import { cn } from '@/lib/utils';
import { useConfigStore } from '@/store/configStore';
import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';

const Textarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<'textarea'>>(
  ({ className, onChange, ...props }, ref) => {
    const { config } = useConfigStore();

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Calculate line height based on font size (approximation)
    // For md:text-sm (14px) with some line spacing, ~20px per line is reasonable
    const LINE_HEIGHT = 20;
    const MAX_LINES = 5;

    // Calculate max height: 5 lines + padding (2 * 8px from py-2)
    const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES;

    // Combine refs
    const handleRef = (element: HTMLTextAreaElement) => {
      textareaRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    // Function to adjust height
    const adjustHeight = useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Reset height to calculate the new height
      textarea.style.height = 'auto';

      // Calculate new height
      const scrollHeight = textarea.scrollHeight;

      // Apply height with max limit
      textarea.style.height = `${Math.min(scrollHeight, MAX_HEIGHT) - 16}px`;

      // Enable/disable scrolling based on content height
      textarea.style.overflowY = scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
    }, [MAX_HEIGHT]);

    // Adjust height on mount and when content changes
    useEffect(() => {
      adjustHeight();
    }, [props.value, adjustHeight]);

    // Handle onChange events
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      adjustHeight();
      if (onChange) {
        onChange(e);
      }
    };

    const generalSpecificVariant = config.ui?.generalSpecificVariant;

    return (
      <textarea
        className={cn(
          'flex min-h-[44px] w-full rounded-md border border-input bg-transparent px-3 py-2.5 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          {
            'border-[#C1C1C1]': generalSpecificVariant === 'velio',
          },
          className
        )}
        ref={handleRef}
        onChange={handleChange}
        {...props}
      />
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
